import { PlanMultidayResponse } from './route-optimization.interface';

/**
 * Métricas del plan semanal de preventa.
 *
 * El drawer planifica **el recorrido de un vendedor**, no una flota: contar
 * vehículos daba siempre `1` y ocupaba un cuarto del tablero sin informar. La
 * métrica que el vendedor lee es cuántos clientes ve en un día típico.
 *
 * Módulo puro: entra la respuesta del motor, salen los números. Sin estado.
 */
export interface PlanDashboard {
  /** Promedio de visitas por día trabajado, redondeado. */
  visitsPerDay: number;
  /** Suma de los tramos de todas las rutas del horizonte. */
  distanceKm: number;
  /** Suma de la duración de todas las rutas del horizonte. */
  durationMin: number;
  /** Paradas que el motor logró planificar. */
  planned: number;
  /** Clientes que quedaron sin día asignado. */
  unplanned: number;
}

/**
 * Calcula las métricas de un plan multi-día.
 *
 * `visitsPerDay` divide por los **días con visitas**, no por los días del
 * horizonte: repartir 60 paradas entre los 7 días de la semana cuando solo se
 * trabajan 5 daría 8,6 y subestimaría la carga real de la jornada. Sin ningún
 * día con visitas el promedio es `0`, nunca una división por cero.
 */
export function computePlanDashboard(
  res: PlanMultidayResponse,
  unplanned: number,
): PlanDashboard {
  let distanceKm = 0;
  let durationMin = 0;
  let planned = 0;
  let daysWithVisits = 0;

  for (const day of res?.days ?? []) {
    let stopsToday = 0;
    for (const route of day.routes ?? []) {
      durationMin += route.total_duration_min ?? 0;
      for (const stop of route.stops ?? []) {
        distanceKm += stop.leg_distance_km ?? 0;
        stopsToday += 1;
      }
    }
    if (stopsToday > 0) daysWithVisits += 1;
    planned += stopsToday;
  }

  return {
    visitsPerDay: daysWithVisits ? Math.round(planned / daysWithVisits) : 0,
    distanceKm,
    durationMin,
    planned,
    unplanned,
  };
}
